import { Alert, Badge, Button, Card, LearnMoreLink, Skeleton, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import { useSettings } from '../../../context/settings-context';
import { useSearchCustomers } from '../hooks/use-search-customers';
import type { Customer } from '../types/customer';
import { RoleBadge } from './customer-role-badge';

function CompanyUserDisabledAlert() {
  const { navigationHandler } = useHandlers();

  return (
    <>
      <Alert headingLevel="h3" title={t(`labels:feature_not_enabled`)}>
        {t(
          `features:customers.components.customer_company_users_card.alerts.not_enabled.description`,
        )}
      </Alert>
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={() => navigationHandler.navigate('/configuration/general')}
        >
          {t('buttons:enable')}
        </Button>
      </div>
    </>
  );
}

function CompanyUserTable({ companyUsers }: { companyUsers: Customer[] }) {
  const { navigationHandler } = useHandlers();

  return (
    <Table
      headings={[
        t('features:customers.components.customer_company_users_card.table.headings.name'),
        t('features:customers.components.customer_company_users_card.table.headings.role'),
      ]}
      caption={t('features:customers.components.customer_company_users_card.table.caption')}
    >
      {companyUsers.map((customer, index) => {
        return (
          <Table.Row
            id={customer.id}
            key={customer.id}
            index={index}
            onClick={() => {
              // Open in a new tab via window.open(path, '_blank') because navigationHandler cannot create a new tab.
              // This avoids confusing users: the back button always returns to the Company table.
              const { id } = navigationHandler.params();
              const currentPath = navigationHandler.path();

              if (!id) {
                return;
              }

              const newPath = currentPath.replace(id, customer.id);
              window.open(newPath, '_blank');
            }}
          >
            <Table.Cell>
              <div className="flex flex-row flex-nowrap items-center gap-x-1">
                <Text>
                  {customer.first_name} {customer.last_name}
                </Text>
                <Badge>{customer.email}</Badge>
              </div>
            </Table.Cell>
            <Table.Cell>
              <RoleBadge role={customer.role_object} />
            </Table.Cell>
          </Table.Row>
        );
      })}
    </Table>
  );
}

function CompanyUserAlert({ isError, refetch }: { isError: boolean; refetch: () => void }) {
  if (isError) {
    return (
      <>
        <Alert
          headingLevel="h3"
          variant="error"
          title={t('features:customers.components.customer_company_users_card.alerts.error.title')}
        >
          {t('features:customers.components.customer_company_users_card.alerts.error.description')}
        </Alert>
        <div className="flex justify-end">
          <Button onClick={refetch} icon="refresh" variant="secondary">
            {t('buttons:reload')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <Alert headingLevel="h3">
      <span>
        {t('features:customers.components.customer_company_users_card.alerts.not_set.description')}{' '}
        <LearnMoreLink link="https://docs.sparklayer.io/company-users" />
      </span>
    </Alert>
  );
}

function CustomerCompanyUsersContent({ sparkId }: { sparkId: string }) {
  const settings = useSettings();
  const isCompanyUserEnabled = settings.subAccountsEnabled;

  const {
    data: customers,
    isLoading,
    isError,
    refetch,
  } = useSearchCustomers({
    parent_customer_sparklayer_id: sparkId,
  });

  if (isLoading) {
    return <Skeleton />;
  }

  if (!isCompanyUserEnabled) {
    return <CompanyUserDisabledAlert />;
  }

  if (customers && customers.length > 0) {
    return <CompanyUserTable companyUsers={customers} />;
  }

  return <CompanyUserAlert isError={isError} refetch={() => void refetch()} />;
}

export function CustomerCompanyUsersCard({ sparkId }: { sparkId: string }) {
  return (
    <Card>
      <Card.Header headingLevel="h2">
        {t('features:customers.components.customer_company_users_card.header')}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customers.components.customer_company_users_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/company-users" />
        </Text>
        <CustomerCompanyUsersContent sparkId={sparkId} />
      </Card.Body>
    </Card>
  );
}
